import React, { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { useCountUp } from "@/hooks/useCountUp";
import { getPublicStats, type publicStats } from "@/api/publicService";
import styles from "./StatsBand.module.css";

interface statConfig {
    key: keyof publicStats;
    step: number; // redondea hacia abajo a este múltiplo (1 = exacto)
    suffix: string;
    label: string;
}

const CONFIG: statConfig[] = [
    { key: "students", step: 50, suffix: "+", label: "Estudiantes" },
    { key: "institutions", step: 10, suffix: "+", label: "Colegios" },
    { key: "simulations", step: 50, suffix: "+", label: "Simulacros presentados" },
    { key: "areas", step: 1, suffix: "", label: "Áreas evaluadas" },
];

// Si el backend no responde, valores de respaldo razonables.
const FALLBACK: publicStats = {
    students: 500,
    institutions: 90,
    simulations: 400,
    areas: 5,
};

const floorTo = (n: number, step: number) =>
    step > 1 ? Math.floor(n / step) * step : n;

const StatItem: React.FC<{
    target: number;
    suffix: string;
    label: string;
    start: boolean;
}> = ({ target, suffix, label, start }) => {
    const n = useCountUp(target, start);
    return (
        <div className={styles.item}>
            <span className={styles.value}>
                {n}
                {suffix}
            </span>
            <span className={styles.label}>{label}</span>
        </div>
    );
};

const StatsBand: React.FC = () => {
    const { ref, inView } = useInView<HTMLElement>();
    const [stats, setStats] = useState<publicStats | null>(null);

    useEffect(() => {
        let active = true;
        getPublicStats()
            .then((d) => active && setStats(d))
            .catch(() => active && setStats(FALLBACK));
        return () => {
            active = false;
        };
    }, []);

    // Los contadores arrancan cuando la banda está visible y ya hay datos.
    const start = inView && stats !== null;

    return (
        <section
            ref={ref}
            className={`${styles.band} ${inView ? styles.visible : ""}`}
        >
            {CONFIG.map((c) => {
                const raw = stats ? stats[c.key] : 0;
                return (
                    <StatItem
                        key={c.label}
                        target={floorTo(raw, c.step)}
                        suffix={c.suffix}
                        label={c.label}
                        start={start}
                    />
                );
            })}
        </section>
    );
};

export default StatsBand;
